import React from "react";
import PropTypes from "prop-types";
import {
  ChatPanel,
  ChatCurrentUserProvider,
  ChatDependencyProvider,
  ETCachedUserByIdProvider,
  ChatRoomSubscriptionProvider,
  CachedPoolsProvider,
  useChatNavigationContext,
  chatQueries,
} from "@evertrue/chat";
import { useFluxStore } from "@evertrue/et-flux";
import { usePoolDataResolverMultiple } from "@evertrue/vms-insights";
import PoolStore from "apps/pools/pool-store";
import useIdentity from "apps/auth/use-identity";
import Api from "base/api";
import Config from "config/env";
import Utils from "core/utils/utils";
import { useIsMobile } from "components/respond-to";
import classNames from "classnames";
import ErrorLogger from "utils/error-logger";
import SessionStore from "apps/auth/stores/session-store";
import Tracker from "utils/tracker";

const ApiCalls = {
  fetchUsersById: ({ userIds }) => {
    return Api.AUTH.USER.post({
      urlExtend: "/bulk_fetch",
      params: { limit: 100 },
      data: { id: userIds },
      disableAlerts: true,
    });
  },
};

export const addUserToRoom = poolId => {
  if (!poolId) return Promise.reject("no poolId for add to room");
  return Api.CHAT.ROOM.post({
    urlExtend: `/${poolId}/user`,
  });
};
const mixPanelEvents = {
  leavePoolChat() {
    Tracker.send("chat_action", { type: "leave", chat_type: "pool" });
  },
  globalPlusAdd() {
    Tracker.send("chat_action", { type: "globalPlusAdd" });
  },
  startPoolChat() {
    Tracker.send("chat_action", { type: "start", chat_type: "pool" });
  },
  startDM() {
    Tracker.send("chat_action", {
      chat_type: "individual",
      type: "start",
    });
  },
  viewChatPanel() {
    Tracker.send("chat_action", { type: "view" });
  },
};
const dependencies = {
  app: "vms",
  mixPanelEvents,
  createDM: ({ user_id }) => {
    if (!user_id) return Promise.reject("no user_id for a dm");
    return Api.CHAT.ROOM.post({
      urlExtend: `/dm/${user_id}`,
    });
  },
  addUserToRoom,
  searchContacts: ({ poolIds, searchVal, hasNoUser }) => {
    const contacts_query = chatQueries.CONTACTS_BY_NAME_WITH_ASSIGNMENTS_QUERY(
      searchVal,
      poolIds ? poolIds : undefined,
      hasNoUser,
    );

    return Api.CONTACTS.SEARCH.post({
      data: contacts_query,
      params: { limit: 100 },
    });
  },
};

const poolStoreMapState = () => ({
  pools: PoolStore.getPools(),
});

const logError = (error, componentStack) => {
  ErrorLogger.error("Chat error", { extra: { error, componentStack } });
};

const sessionStoreMapState = () => ({ is_impersonating: SessionStore.getIsImpersonating() });
const ChatController = ({ onClick = () => {}, buttonClassName }) => {
  const { user = {}, org, session = {} } = useIdentity();
  const { pools } = useFluxStore(PoolStore, poolStoreMapState);

  const is_mobile = useIsMobile();
  const [chat_nav_state, chatNavDispatch] = useChatNavigationContext();
  const { is_impersonating } = useFluxStore(SessionStore, sessionStoreMapState);

  // ensure settings are loadded
  const { settings } = usePoolDataResolverMultiple(pools.map(i => i.id));
  const pools_with_chat_enabled = pools.filter(pool => {
    const pool_settings = settings[pool.id] || {};
    return pool_settings.chat_enabled;
  });

  const deps = React.useMemo(() => {
    //  it needs to be a promise to follow the same format as core
    const getPools = async (query = "") => {
      const results = pools_with_chat_enabled.filter(pool => {
        return Utils.searchCompare(pool.name, query);
      });
      return { items: results, total: results.length };
    };

    return {
      ...dependencies,
      isMobile: is_mobile,
      getPools,
      userId: user.id,
      oid: org.id,
      appKey: Config.appKey,
      env: Config.dataEnv.toLowerCase(),
      authorization: session.token,
      fetchPoolsById: async ({ pool_ids }) => {
        // core calls the network but here we have all pools
        return pools_with_chat_enabled;
      },
    };
  }, [is_mobile, org.id, pools_with_chat_enabled, session.token, user.id]);

  // this is for the error boundary in chat
  React.useEffect(() => {
    if (chat_nav_state.reset_chat) {
      // reset reset state
      chatNavDispatch({ type: "RESET_CHAT", payload: false });
    }
  }, [chatNavDispatch, chat_nav_state.reset_chat]);

  if (is_impersonating) return null;
  // if true we unmount chat to reset everything
  if (chat_nav_state.reset_chat) return null;

  if (!pools_with_chat_enabled.length) return null;

  return (
    <div style={{ margin: "0 14px" }}>
      <ChatDependencyProvider dependencies={deps}>
        <ETCachedUserByIdProvider fetchUsersById={ApiCalls.fetchUsersById}>
          <CachedPoolsProvider fetchPoolsById={deps.fetchPoolsById}>
            <ChatCurrentUserProvider>
              <ChatRoomSubscriptionProvider>
                <ChatPanel
                  onClick={onClick}
                  buttonClassName={buttonClassName}
                  className={classNames({
                    "chat-controller--panel-mobile": is_mobile,
                    "is-full-height": chat_nav_state.url === "room",
                  })}
                  onChatError={logError}
                />
              </ChatRoomSubscriptionProvider>
            </ChatCurrentUserProvider>
          </CachedPoolsProvider>
        </ETCachedUserByIdProvider>
      </ChatDependencyProvider>
    </div>
  );
};

ChatController.propTypes = {
  onClick: PropTypes.func,
  buttonClassName: PropTypes.string,
};

export default ChatController;
